import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { TanTable } from '../../components/TanTable';
import TabBar from '../../components/TabBar';
import PermissionConfigurable from '../../components/PermissionConfigurable';
import { usePermissionsMode } from '../../contexts/PermissionsModeContext';
import safetyReportsService from '../../services/safetyReports.service';
import { saveAs } from '../../utils/saveFile';
import { reportError } from '../accounting/reports/reportUtils';
import type { SafetyReportCatalogItem, SafetyReportRow, SafetyReportRunResult, SafetyReportSheet } from '../../types/safetySystem';
import ReportParamsForm, { type ReportParamValues } from './components/ReportParamsForm';

/**
 * Safety Report Center — port of the V1 Safety "Reports" menu (Phase 4).
 * Left: the catalog (server-filtered to what the caller may run; deferred reports are
 * listed disabled with the reason). Right: the selected report's parameters, then the
 * result as one grid per sheet. "Download Excel" re-runs the same parameters on the
 * server's xlsx route, so the workbook always matches what is on screen.
 * Selection lives in the URL (`?report=<id>`) so the Hub and bookmarks can deep-link.
 */

const NUMERIC_KINDS = new Set(['num', 'dec1', 'dec2']);
const ACCESS_DENIED = 'You do not have access to this report.';

type GridRow = SafetyReportRow & { _rowid: string };

export function sheetColumns(sheet: SafetyReportSheet): ColumnDef<GridRow>[] {
  return sheet.columns.map((c) => {
    const numeric = NUMERIC_KINDS.has(c.kind);
    return {
      id: c.key,
      accessorKey: c.key,
      header: c.header,
      size: Math.max(70, Math.round((c.width || 12) * 7)),
      cell: ({ getValue }) => {
        const v = getValue();
        const text = v === null || v === undefined ? '' : String(v);
        return numeric ? <span className="block text-right tabular-nums">{text}</span> : text;
      },
    } as ColumnDef<GridRow>;
  });
}

export function errorMessageFor(error: unknown, fallback: string): string {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 403) return ACCESS_DENIED;
  return reportError(error, fallback);
}

const byTitle = (a: SafetyReportCatalogItem, b: SafetyReportCatalogItem) => a.title.localeCompare(b.title);

export default function SafetyReportCenter() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setPageComponents, permissionsMode } = usePermissionsMode();

  // ---- Catalog -------------------------------------------------------------
  const [catalog, setCatalog] = useState<SafetyReportCatalogItem[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState('');
  const [catalogTick, setCatalogTick] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setCatalogLoading(true);
    setCatalogError('');
    safetyReportsService.catalog(controller.signal)
      .then((res) => { if (!controller.signal.aborted) setCatalog([...(res.data ?? [])].sort(byTitle)); })
      .catch((e) => {
        if (controller.signal.aborted) return;
        setCatalog([]);
        setCatalogError(errorMessageFor(e, 'Unable to load the report list. Retry or check your access with an administrator.'));
      })
      .finally(() => { if (!controller.signal.aborted) setCatalogLoading(false); });
    return () => controller.abort();
  }, [catalogTick]);

  // One permission component per report so the permissions UI can grant them.
  const pageComponentsList = useMemo(
    () => catalog.map((r) => ({ id: `safety-system-report-${r.id}`, name: r.title })),
    [catalog],
  );
  useEffect(() => {
    setPageComponents(pageComponentsList);
    return () => setPageComponents([]);
  }, [pageComponentsList, setPageComponents]);

  // ---- Selection (URL) -----------------------------------------------------
  const selectedId = searchParams.get('report') || '';
  const selected = useMemo(() => catalog.find((r) => r.id === selectedId) ?? null, [catalog, selectedId]);

  const select = useCallback((id: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set('report', id); else next.delete('report');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // ---- Run -----------------------------------------------------------------
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState('');
  const [result, setResult] = useState<SafetyReportRunResult | null>(null);
  const [lastValues, setLastValues] = useState<ReportParamValues | null>(null);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  // A different report: the previous result no longer applies.
  useEffect(() => {
    setResult(null); setLastValues(null); setRunError(''); setDownloadError(''); setSheetIndex(0);
  }, [selectedId]);

  const run = useCallback((values: ReportParamValues) => {
    if (!selected) return;
    const id = selected.id;
    setRunning(true);
    setRunError('');
    setDownloadError('');
    safetyReportsService.run(id, values)
      .then((res) => { setResult(res); setLastValues(values); setSheetIndex(0); })
      .catch((e) => { setResult(null); setLastValues(null); setRunError(errorMessageFor(e, 'Failed to run the report. Please try again.')); })
      .finally(() => setRunning(false));
  }, [selected]);

  const download = useCallback(async () => {
    if (!selected || !lastValues || !result) return;
    const id = selected.id;
    setDownloading(true);
    setDownloadError('');
    try {
      // The run response already carries the server's filename (V1's
      // `<Prefix>_<userid>_<date>.xlsx`), so Save As can open before the bytes are fetched.
      const suggested = result.filename || `${id}.xlsx`;
      await saveAs(suggested, () => safetyReportsService.downloadXlsx(id, lastValues).then((r) => r.blob));
    } catch (e) {
      setDownloadError(errorMessageFor(e, 'Failed to download the workbook. Please try again.'));
    } finally {
      setDownloading(false);
    }
  }, [selected, lastValues, result]);

  // ---- Grid ----------------------------------------------------------------
  const sheets = result?.sheets ?? [];
  const activeSheet = sheets[Math.min(sheetIndex, Math.max(sheets.length - 1, 0))] ?? null;
  const columns = useMemo(() => (activeSheet ? sheetColumns(activeSheet) : []), [activeSheet]);
  const rows = useMemo<GridRow[]>(
    () => (activeSheet ? activeSheet.rows.map((r, i) => ({ ...r, _rowid: String(i) })) : []),
    [activeSheet],
  );
  const tabs = useMemo(
    () => sheets.map((s, i) => ({ id: String(i), label: `${s.name} (${s.count})`, current: i === sheetIndex })),
    [sheets, sheetIndex],
  );

  return (
    <main className="flex h-[calc(100vh-7rem)] min-h-[450px] flex-col gap-2 overflow-hidden p-2 sm:p-4 text-gray-900 dark:text-gray-100">
      <header className="flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Safety Report Center</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Pick a report, set its parameters, run it on screen, then download the Excel workbook.</p>
        </div>
        <button
          type="button"
          onClick={() => setCatalogTick((t) => t + 1)}
          disabled={catalogLoading}
          className="inline-flex items-center gap-1.5 rounded-md border border-rule px-3 py-1.5 text-sm hover:bg-highlight disabled:cursor-not-allowed disabled:text-disabled"
        >
          <ArrowPathIcon width={16} aria-hidden="true" />
          {catalogLoading ? 'Loading…' : 'Refresh list'}
        </button>
      </header>
      {permissionsMode && (
        <PermissionConfigurable componentId="safety-system-payroll-sensitive" componentName="Payroll Sensitive Data" defaultHidden>
          <p className="rounded-md border border-rule bg-panel p-3 text-sm">
            Payroll Sensitive Data — level 2 reveals full SSN / Tax ID values in reports and exports, and enables other Safety sensitive-data controls.
          </p>
        </PermissionConfigurable>
      )}
      {catalogError && <div role="alert" className="shrink-0 rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{catalogError}</div>}

      <div className="flex min-h-0 flex-1 gap-3">
        {/* Catalog */}
        <nav aria-label="Reports" className="flex w-64 shrink-0 flex-col overflow-y-auto rounded-md border border-rule bg-panel">
          {!catalogLoading && catalog.length === 0 && !catalogError && (
            <p className="p-3 text-sm text-gray-500 dark:text-gray-400">No reports are available to you.</p>
          )}
          <ul className="divide-y divide-rule">
            {catalog.map((r) => {
              const active = r.id === selectedId;
              return (
                <li key={r.id}>
                  <button
                    type="button"
                    disabled={!r.available}
                    title={r.available ? undefined : (r.reason || 'This report is not available.')}
                    aria-current={active ? 'page' : undefined}
                    onClick={() => select(r.id)}
                    className={`block w-full px-3 py-2 text-left text-sm hover:bg-highlight disabled:cursor-not-allowed disabled:text-disabled disabled:hover:bg-transparent ${active ? 'bg-highlight font-semibold' : ''}`}
                  >
                    {r.title}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Selected report */}
        <section className="flex min-w-0 flex-1 flex-col gap-2">
          {!selected ? (
            <p className="rounded-md border border-rule bg-panel p-4 text-sm text-gray-500 dark:text-gray-400">
              {catalogLoading ? 'Loading reports…' : 'Select a report from the list.'}
            </p>
          ) : (
            <>
              <div className="shrink-0 rounded-md border border-rule bg-panel p-3">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <PermissionConfigurable componentId={`safety-system-report-${selected.id}`} componentName={selected.title} defaultHidden>
                    <h2 className="text-base font-semibold">{selected.title}</h2>
                  </PermissionConfigurable>
                  <button
                    type="button"
                    onClick={() => { void download(); }}
                    disabled={!result || !lastValues || downloading || running}
                    className="inline-flex items-center gap-1.5 rounded-md border border-rule px-3 py-1.5 text-sm hover:bg-highlight disabled:cursor-not-allowed disabled:text-disabled"
                  >
                    <ArrowDownTrayIcon width={16} aria-hidden="true" />
                    {downloading ? 'Preparing…' : 'Download Excel'}
                  </button>
                </div>
                <ReportParamsForm key={selected.id} params={selected.params} onRun={run} running={running} />
              </div>
              {runError && <div role="alert" className="shrink-0 rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{runError}</div>}
              {downloadError && <div role="alert" className="shrink-0 rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{downloadError}</div>}

              {result && (
                <>
                  {result.notes.length > 0 && (
                    <ul aria-label="Report notes" className="shrink-0 list-disc space-y-0.5 rounded-md border border-rule bg-panel px-3 py-2 pl-7 text-sm text-gray-600 dark:text-gray-300">
                      {result.notes.map((n, i) => <li key={i}>{n}</li>)}
                    </ul>
                  )}
                  {sheets.length > 1 && (
                    <TabBar tabs={tabs} onTabChange={(id) => setSheetIndex(Number(id))} compact noBorder className="shrink-0" />
                  )}
                  {activeSheet?.title && <p className="shrink-0 text-sm font-semibold">{activeSheet.title}</p>}
                  {activeSheet && (
                    <div className="min-h-0 flex-1">
                      <TanTable<GridRow>
                        data={rows}
                        columns={columns}
                        loading={running}
                        fillContainer
                        cardTitle={`${selected.title} — ${activeSheet.name}`}
                        settingsKey={`safety-report-${selected.id}-${sheetIndex}`}
                        fieldName={`safety-report-${selected.id}-${sheetIndex}`}
                        searchPlaceholder="Filter results…"
                        enableColumnFilters
                        enableExcelExport={false}
                        enableShowHideCols
                        enableRowsPerPage
                        enableZebraStriping
                        defaultPageSize={100}
                        getRowId={(r) => r._rowid}
                        getRowClassName={(r) => (r._kind ? 'font-bold' : '')}
                      />
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </section>
      </div>
    </main>
  );
}
